"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import type { SegmentRef } from "@engine/render";
import type { PuzzleView } from "@/server/puzzles";

interface Props {
  /** Which premise flavours the wording — premises.<key> in the catalogues. */
  premiseKey: string;
  puzzle: PuzzleView;
  selected: number;
  onSelect: (char: number) => void;
  placements: ReadonlyArray<number | null>;
  /** Point the board at what a clue is talking about; null clears it. */
  onHighlight: (ref: SegmentRef | null) => void;
  /** The reference currently being pointed at, so it can be shown as active. */
  active: SegmentRef | null;
}

const sameRef = (a: SegmentRef | null, b: SegmentRef | undefined): boolean =>
  a !== null && b !== undefined && JSON.stringify(a) === JSON.stringify(b);

/**
 * The suspect cards.
 *
 * Selecting one says who you are placing. The emphasised words inside a clue are live: pointing at
 * "the Library" or "the second column" lights up those squares on the board. Tap works as well as
 * hover, because half the people playing this have no pointer at all.
 */
export function ClueList({ premiseKey, puzzle, selected, onSelect, placements, onHighlight, active }: Props) {
  const t = useTranslations("game");
  const premise = useTranslations(`premises.${premiseKey}`);
  /** A reference the player has tapped, which stays lit after the pointer moves away. */
  const [pinned, setPinned] = useState<SegmentRef | null>(null);

  return (
    <section className="clue-column" aria-label={premise("suspectsHeading")}>
      <h2 data-testid="suspects-heading" style={{ fontSize: "1rem", margin: 0 }}>
        {premise("suspectsHeading")}
      </h2>
      {puzzle.characters.map((character) => {
        const clue = puzzle.renderedClues[character.index];
        const isSelected = selected === character.index;
        const isPlaced = placements[character.index] !== null;

        return (
          <div
            key={character.index}
            data-testid={`suspect-${character.letter}`}
            // The whole card selects, not just the letter: on a phone the disc is a small target
            // and the card is the thing you are looking at. Clue keywords keep their own job —
            // they point at the board — so a click on one is left alone.
            onClick={(event) => {
              if ((event.target as HTMLElement).closest('[data-clue-ref="true"]')) return;
              onSelect(character.index);
            }}
            // Styling lives in globals.css so the wide layout can compact these rows; an inline
            // border or padding here would beat every rule there, which is what kept them boxy.
            className={`clue-card${isSelected ? " is-selected" : ""}${isPlaced ? " is-placed" : ""}`}
            data-placed={isPlaced}
          >
            {/* The green bar says this out of the corner of the eye; this says it to anyone the
                colour does not reach. Colour must never be the only carrier of a state. */}
            {isPlaced && (
              <span className="visually-hidden" data-testid={`placed-${character.letter}`}>
                {t("placedOnBoard")}
              </span>
            )}
            <button
              type="button"
              data-testid={`select-${character.letter}`}
              aria-pressed={isSelected}
              aria-label={character.name}
              onClick={() => onSelect(character.index)}
              className="clue-disc"
              style={{ background: character.isVictim ? "var(--victim)" : "var(--suspect)" }}
            >
              {character.letter}
            </button>

            <span>
              <button type="button" className="clue-name" onClick={() => onSelect(character.index)}>
                {character.name}
              </button>

              <span className="clue-text">
                {clue && clue.segments.length > 0
                  ? clue.segments.map((segment, index) =>
                      segment.ref ? (
                        <button
                          key={index}
                          type="button"
                          data-clue-ref="true"
                          data-testid={`clue-ref-${character.letter}-${index}`}
                          // What this word points at, so the board — and a test — can tell a room
                          // from a person who is not on the board yet.
                          data-ref-type={segment.ref.type}
                          // Pointing works by hover for a mouse and by tap for a finger.
                          // Hovering points temporarily; clicking pins it so it survives the
                          // pointer leaving. Without the distinction a mouse user would hover
                          // (pointing), then click — and the click would read as "already on" and
                          // switch it straight back off.
                          onPointerEnter={() => !pinned && onHighlight(segment.ref ?? null)}
                          onPointerLeave={() => !pinned && onHighlight(null)}
                          onFocus={() => !pinned && onHighlight(segment.ref ?? null)}
                          onBlur={() => !pinned && onHighlight(null)}
                          onClick={(event) => {
                            // Pointing at a clue must not also change who is selected.
                            event.stopPropagation();
                            const isPinnedHere = sameRef(pinned, segment.ref as SegmentRef);
                            setPinned(isPinnedHere ? null : (segment.ref ?? null));
                            onHighlight(isPinnedHere ? null : (segment.ref ?? null));
                          }}
                          style={{
                            background: sameRef(active, segment.ref) ? "var(--accent)" : "transparent",
                            color: sameRef(active, segment.ref) ? "#fff" : "var(--fg)",
                            border: "none",
                            borderBottom: "1px dotted currentColor",
                            padding: "0 1px",
                            font: "inherit",
                            fontWeight: 700,
                            cursor: "pointer",
                            borderRadius: "0.2rem",
                          }}
                        >
                          {segment.text}
                        </button>
                      ) : segment.strong ? (
                        <strong key={index} style={{ color: "var(--fg)" }}>
                          {segment.text}
                        </strong>
                      ) : (
                        <span key={index}>{segment.text}</span>
                      ),
                    )
                  : t("noClue")}
              </span>
            </span>
          </div>
        );
      })}

      {puzzle.renderedGeneralClues.length > 0 && (
        <div
          data-testid="general-clues"
          style={{
            marginTop: "0.5rem",
            padding: "0.6rem 0.75rem",
            border: "1px dashed rgba(128,128,128,0.5)",
            borderRadius: "0.5rem",
          }}
        >
          <strong style={{ display: "block", marginBottom: "0.25rem" }}>{t("generalClues")}</strong>
          {puzzle.renderedGeneralClues.map((segments, index) => (
            <p key={index} style={{ margin: 0, color: "var(--muted)" }}>
              {segments.map((segment, i) =>
                segment.strong ? (
                  <strong key={i} style={{ color: "var(--fg)" }}>
                    {segment.text}
                  </strong>
                ) : (
                  <span key={i}>{segment.text}</span>
                ),
              )}
            </p>
          ))}
        </div>
      )}
    </section>
  );
}
